import { FormContext, iControlProps } from "../types";
import React, { useState } from "react";
import { TextInputV2 } from "@boi/react-components";

import Container from "./container";
import { Column, Flexbox } from "../../theme/grid";
import { StyledRowError } from "./styled/row-container";
import GravityFieldContainer from "../../gravity-form/field/field-component";
import { Select } from ".";
import { isValidName } from "../rules/valid-name";
import { replacePunctuationCharacters } from "../helper";

const isEmptyOrHiddenName = (name: string, isHidden: boolean | undefined) => {
    return isHidden || name === "";
};

type PrefixOption = {
    text: string;
    value: string;
    isSelected?: boolean;
};

type NameSubField = {
    id: number;
    label: string;
    customLabel?: string;
    isHidden?: boolean;
    placeholder?: string;
    defaultValue?: string;
    choices?: Array<PrefixOption>;
};

type NameProps = iControlProps & {
    context: FormContext;
    id: string;
    formId: string;
    type: string;
    name: string;
    isRequired: boolean;
    errorMessage?: string;
    label?: string;
    description?: string;
    customTooltip?: string;
    inputs: Array<NameSubField>;
};

const NameField = ({
    context,
    formId,
    name,
    isRequired,
    inputs,
    value = {},
    ...props
}: NameProps) => {
    const [prefix, setPrefix] = useState(value[inputs[0].id] || "");
    const [first, setFirst] = useState(value[inputs[1].id] || "");
    const [middle, setMiddle] = useState(value[inputs[2].id] || "");
    const [last, setLast] = useState(value[inputs[3].id] || "");
    const [suffix, setSuffix] = useState(value[inputs[4].id] || "");
    const [errorMessage, setErrorMessage] = useState("");
    const [prefixError, setPrefixError] = useState(false);
    const [firstError, setFirstError] = useState(false);
    const [middleError, setMiddleError] = useState(false);
    const [lastError, setLastError] = useState(false);
    const [suffixError, setSuffixError] = useState(false);

    const changeValues = () => {
        context.unsetValue(name);
        setPrefixError(false);
        setFirstError(false);
        setMiddleError(false);
        setLastError(false);
        setSuffixError(false);

        const errorWith = [];

        if (!isValidName(first)) {
            setFirstError(true);
            errorWith.push("first name");
        }
        if (!isValidName(middle)) {
            setMiddleError(true);
            errorWith.push("middle name");
        }
        if (!isValidName(last)) {
            setLastError(true);
            errorWith.push("last name");
        }
        if (!isValidName(suffix)) {
            setSuffixError(true);
            errorWith.push("suffix");
        }
        if (errorWith.length) {
            if (props.errorMessage) {
                setErrorMessage(props.errorMessage);
            } else {
                setErrorMessage(
                    "Looks like you've entered an invalid character on " +
                        errorWith.join(", ") +
                        "."
                );
            }
        } else {
            if ((first === "" || last === "") && (
                isRequired || first !== "" || last !== "" ||
                !isEmptyOrHiddenName(prefix, inputs[0].isHidden) ||
                !isEmptyOrHiddenName(middle, inputs[2].isHidden) ||
                !isEmptyOrHiddenName(suffix, inputs[4].isHidden)
            )) {
                first === "" && setFirstError(true);
                last === "" && setLastError(true);
    
                if (isRequired) {
                    setErrorMessage("This field is required. Please enter your First and Last name.");
                } else {
                    setErrorMessage(
                        "This field is optional, if you wish to provide your name, please enter both First and Last name."
                    );
                }
            } else {
                setErrorMessage("");
            }
        }

        props.onChange &&
            props.onChange(
                Object.assign(
                    {},
                    inputs[0] &&
                        !inputs[0].isHidden && { [inputs[0].id]: prefix },
                    inputs[1] &&
                        !inputs[1].isHidden && { [inputs[1].id]: first },
                    inputs[2] &&
                        !inputs[2].isHidden && { [inputs[2].id]: middle },
                    inputs[3] &&
                        !inputs[3].isHidden && { [inputs[3].id]: last },
                    inputs[4] &&
                        !inputs[4].isHidden && { [inputs[4].id]: suffix }
                )
            );
    };

    return (
        <Container
            id={props.id}
            type={props.type}
            isRequired={isRequired}
            label={props.label}
            description={props.description}
            customTooltip={props.customTooltip}
            cssClass={props.cssClass}
        >
            <Flexbox
                className="name-field"
                mx="0"
                onBlur={() => {
                    changeValues();
                }}
                order="3"
            >
                {inputs[0] && !inputs[0].isHidden && (
                    <>
                        {/*
                        // @ts-ignore */}
                        <Column xs="12" md="6" className="u-mb-3">
                            <GravityFieldContainer
                                label={
                                    inputs[0].customLabel
                                        ? inputs[0].customLabel
                                        : inputs[0].label
                                }
                                labelClass={"field-sub-label"}
                                isRequired="true"
                                id={inputs[0].id}
                                formId={formId}
                                cssClass="u-pb-0"
                                showError={false}
                            >
                                <Select
                                    id={`input_${formId}_${inputs[0].id}`}
                                    placeholder={inputs[0].placeholder}
                                    value={prefix}
                                    choices={inputs[0].choices}
                                    error={prefixError}
                                    showError={false}
                                    onFocus={() => {
                                        setPrefixError(false);
                                        setErrorMessage("");
                                    }}
                                    onChange={(newValue?: string) => {
                                        setPrefix(newValue);
                                    }}
                                    cssClass="u-p-0"
                                />
                            </GravityFieldContainer>
                        </Column>
                        {/*
                            // @ts-ignore */}
                        <Column xs="12" md="6" className={"hide-xs-down"} />
                    </>
                )}
                {inputs[1] && !inputs[1].isHidden && (
                    <>
                        {/*
                            // @ts-ignore */}
                        <Column xs="12" md="6" className="u-mb-3">
                            <GravityFieldContainer
                                label={
                                    inputs[1].customLabel
                                        ? inputs[1].customLabel
                                        : inputs[1].label
                                }
                                labelClass={"field-sub-label"}
                                error={firstError}
                                showError={false}
                                isRequired="true"
                                id={inputs[1].id}
                                formId={formId}
                                cssClass="u-pb-0"
                            >
                                <TextInputV2
                                    value={first}
                                    placeholder={inputs[1].placeholder}
                                    onChange={(newValue: string) => {
                                        setFirst(
                                            replacePunctuationCharacters(newValue)
                                        );
                                    }}
                                    onFocus={() => {
                                        setFirstError(false);
                                        setErrorMessage("");
                                    }}
                                    type="text"
                                    id={`input_${formId}_${inputs[1].id}`}
                                    // remove this aria-label if the Container component has deleted and the label and other props are passed to TextInputV2
                                    aria-label={props.label}
                                    isInvalid={!!firstError}
                                />
                            </GravityFieldContainer>
                        </Column>
                    </>
                )}
                {inputs[2] && !inputs[2].isHidden && (
                    <>
                        {/*
                            // @ts-ignore */}
                        <Column xs="12" md="6" className="u-mb-3">
                            <GravityFieldContainer
                                label={
                                    inputs[2].customLabel
                                        ? inputs[2].customLabel
                                        : inputs[2].label
                                }
                                labelClass={"field-sub-label"}
                                error={middleError}
                                showError={false}
                                isRequired="true"
                                id={inputs[2].id}
                                formId={formId}
                                cssClass="u-pb-0"
                            >
                                <TextInputV2
                                    value={middle}
                                    placeholder={inputs[2].placeholder}
                                    onChange={(newValue: string) => {
                                        setMiddle(
                                            replacePunctuationCharacters(newValue)
                                        );
                                    }}
                                    onFocus={() => {
                                        setMiddleError(false);
                                        setErrorMessage("");
                                    }}
                                    type="text"
                                    id={`input_${formId}_${inputs[2].id}`}
                                    // remove this aria-label if the Container component has deleted and the label and other props are passed to TextInputV2
                                    aria-label={props.label}
                                    isInvalid={!!middleError}
                                />
                            </GravityFieldContainer>
                        </Column>
                    </>
                )}
                {inputs[3] && !inputs[3].isHidden && (
                    <>
                        {/*
                            // @ts-ignore */}
                        <Column xs="12" md="6" className="u-mb-3">
                            <GravityFieldContainer
                                label={
                                    inputs[3].customLabel
                                        ? inputs[3].customLabel
                                        : inputs[3].label
                                }
                                labelClass={"field-sub-label"}
                                error={lastError}
                                showError={false}
                                isRequired="true"
                                id={inputs[3].id}
                                formId={formId}
                                cssClass="u-pb-0"
                            >
                                <TextInputV2
                                    value={last}
                                    placeholder={inputs[3].placeholder}
                                    onChange={(newValue: string) => {
                                        setLast(
                                            replacePunctuationCharacters(newValue)
                                        );
                                    }}
                                    onFocus={() => {
                                        setLastError(false);
                                        setErrorMessage("");
                                    }}
                                    type="text"
                                    id={`input_${formId}_${inputs[3].id}`}
                                    // remove this aria-label if the Container component has deleted and the label and other props are passed to TextInputV2
                                    aria-label={props.label}
                                    isInvalid={!!lastError}
                                />
                            </GravityFieldContainer>
                        </Column>
                    </>
                )}
                {inputs[4] && !inputs[4].isHidden && (
                    <>
                        {/*
                            // @ts-ignore */}
                        <Column xs="12" md="6" className="u-mb-3">
                            <GravityFieldContainer
                                label={
                                    inputs[4].customLabel
                                        ? inputs[4].customLabel
                                        : inputs[4].label
                                }
                                labelClass={"field-sub-label"}
                                error={suffixError}
                                showError={false}
                                isRequired="true"
                                id={inputs[4].id}
                                formId={formId}
                                cssClass="u-pb-0"
                            >
                                <TextInputV2
                                    value={suffix}
                                    placeholder={inputs[4].placeholder}
                                    onChange={(newValue: string) => {
                                        setSuffix(
                                            replacePunctuationCharacters(newValue)
                                        );
                                    }}
                                    onFocus={() => {
                                        setSuffixError(false);
                                        setErrorMessage("");
                                    }}
                                    type="text"
                                    id={`input_${formId}_${inputs[4].id}`}
                                    // remove this aria-label if the Container component has deleted and the label and other props are passed to TextInputV2
                                    aria-label={props.label}
                                    isInvalid={!!suffixError}
                                />
                            </GravityFieldContainer>
                        </Column>
                    </>
                )}
            </Flexbox>
            {errorMessage && (
                <StyledRowError className="field-error u-mt-minus-2">
                    {errorMessage}
                </StyledRowError>
            )}
        </Container>
    );
};
export default NameField;
